/**
 * Zoomable image preview panel for Candid RPA status (CMM screenshot).
 *
 * Mirrors {@link CandidRpaAuthLetterPdfPanel} layout and owns load state
 * using the same Zoom + img pattern as {@link ImageviewerModal}.
 *
 * @module candidRpaImageViewerPanel
 */
import { useEffect, useState } from "react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

/** Props for {@link CandidRpaImageViewerPanel}. */
export interface CandidRpaImageViewerPanelProps {
  /** Image URL to display. */
  url: string;
  /** Document label shown in the panel header. */
  name?: string;
  /** Timestamp or relative time shown in the panel header. */
  time?: string;
}

/**
 * Renders a zoomable screenshot with a name/time header and load overlay.
 *
 * @param props.url - Image source URL
 * @param props.name - Header document name
 * @param props.time - Header timestamp label
 * @returns Image viewer panel
 */
const CandidRpaImageViewerPanel = ({
  url,
  name = "Name",
  time = "Time",
}: CandidRpaImageViewerPanelProps) => {
  const [isLoading, setIsLoading] = useState(Boolean(url));

  useEffect(() => {
    setIsLoading(Boolean(url));
  }, [url]);

  /**
   * Clears the loading overlay once the image has finished loading.
   */
  const handleImageLoad = () => {
    setIsLoading(false);
  };

  /**
   * Clears the loading overlay when the image fails so the panel is not stuck.
   */
  const handleImageError = () => {
    setIsLoading(false);
  };

  return (
    <div className="image-viewer-for-candid-rpa flex h-full flex-col overflow-hidden">
      <div className="image-viewer-header flex items-center justify-between border-b border-primaryGray-14 px-4 py-2 text-tiny font-semibold">
        <div className="image-viewer-name">{name}</div>
        <div className="image-viewer-time">{time}</div>
      </div>
      <div className="image-viewer-content relative min-h-0 flex-1 overflow-auto">
        {!url ? (
          <div className="flex h-full items-center justify-center text-sm text-primaryGray-6">
            No image available
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
                <div className="text-center">
                  <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-gray-900"></div>
                  <div className="text-sm text-gray-500">Loading image...</div>
                </div>
              </div>
            )}
            <div className="relative w-full p-4">
              <Zoom>
                <img
                  src={url}
                  alt={name}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  className="block w-full"
                />
              </Zoom>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CandidRpaImageViewerPanel;
